import React, { useEffect, useState } from 'react';

export default function SampleFeasibilityViewer({ isOpen, onClose }) {
  const [currentPage, setCurrentPage] = useState(0);
  const [isProtected, setIsProtected] = useState(true);
  
  // Updated with actual file names
  const samplePages = [
    {
      url: '/samples/feasibility/Page-1.jpg',
      title: 'Cover Page',
      description: 'Professional feasibility assessment for residential development'
    },
    {
      url: '/samples/feasibility/Page-2.jpg',
      title: 'Executive Summary',
      description: 'Key findings and development potential summary'
    },
    {
      url: '/samples/feasibility/Page-3.jpg',
      title: 'Site Information',
      description: 'Property details and site characteristics'
    },
    {
      url: '/samples/feasibility/Page-4.jpg',
      title: 'Zoning & Planning Controls',
      description: 'Local Environmental Plan and Development Control Plan'
    },
    {
      url: '/samples/feasibility/Page-5.jpg',
      title: 'Site Constraints',
      description: 'Environmental and planning constraints analysis'
    },
    {
      url: '/samples/feasibility/Page-6.jpg',
      title: 'Development Potential',
      description: 'Feasible development options and recommendations'
    },
    {
      url: '/samples/feasibility/Page-7.jpg',
      title: 'Compliance Analysis',
      description: 'Preliminary compliance with planning controls'
    },
    {
      url: '/samples/feasibility/Page-8.jpg',
      title: 'Concept Layout',
      description: 'Indicative site layout and configuration'
    },
    {
      url: '/samples/feasibility/Page-9.jpg',
      title: 'Next Steps',
      description: 'Recommendations and project timeline'
    },
    {
      url: '/samples/feasibility/Page-10.jpg',
      title: 'Disclaimer',
      description: 'Professional disclaimers and limitations'
    },
    {
      url: '/samples/feasibility/Page-11.jpg',
      title: 'Contact Information',
      description: 'Professional contact details'
    }
  ];

  const goToPrevious = () => {
    setCurrentPage(prev => (prev > 0 ? prev - 1 : samplePages.length - 1));
  };

  const goToNext = () => {
    setCurrentPage(prev => (prev < samplePages.length - 1 ? prev + 1 : 0));
  };

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Protection implementation
  useEffect(() => {
    if (!isOpen || !isProtected) return;

    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    const handleSelectStart = (e) => {
      e.preventDefault();
      return false;
    };

    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    const handleKeyDown = (e) => {
      if (e.ctrlKey && (e.key === 'p' || e.key === 's')) {
        e.preventDefault();
        return false;
      }
      if (e.keyCode === 123) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('keydown', handleKeyDown);

    const style = document.createElement('style');
    style.innerHTML = `
      .feasibility-viewer * {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
      }
      
      @media print {
        .feasibility-viewer {
          display: none !important;
        }
      }
      
      .feasibility-viewer img {
        pointer-events: none !important;
        -webkit-user-drag: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('keydown', handleKeyDown);
      style.remove();
    };
  }, [isOpen, isProtected]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center feasibility-viewer">
      <div className="bg-white rounded-lg max-w-5xl max-h-[95vh] w-full mx-4 overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-green-800 to-green-600 text-white p-4 flex-shrink-0">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold">Sample Feasibility Report</h3>
            <button onClick={onClose} className="text-2xl hover:text-gray-200">×</button>
          </div>
        </div>

        {/* Document Display with Arrow Navigation */}
        <div className="relative bg-gray-100 flex-grow flex items-center justify-center" style={{ height: '75vh' }}>
          
          {/* Left Arrow */}
          <button
            onClick={goToPrevious}
            className="absolute left-4 z-10 bg-white hover:bg-gray-100 text-gray-800 p-3 rounded-full shadow-lg transition-all hover:scale-110"
            aria-label="Previous page"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Report Page Image */}
          <div className="h-full flex items-center justify-center p-4">
            <img
              src={samplePages[currentPage].url}
              alt={samplePages[currentPage].title}
              className="max-h-full max-w-full object-contain shadow-lg"
              draggable={false}
            />
          </div>

          {/* Right Arrow */}
          <button
            onClick={goToNext}
            className="absolute right-4 z-10 bg-white hover:bg-gray-100 text-gray-800 p-3 rounded-full shadow-lg transition-all hover:scale-110"
            aria-label="Next page"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Page Counter and Info */}
        <div className="bg-white p-3 border-t flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-semibold text-gray-800">{samplePages[currentPage].title}</p>
              <p className="text-xs text-gray-500">{samplePages[currentPage].description}</p>
            </div>
            <div className="text-sm text-gray-600 font-medium">
              Page {currentPage + 1} of {samplePages.length}
            </div>
          </div>
        </div>

        {/* Footer Warning */}
        <div className="bg-yellow-50 p-3 border-t flex-shrink-0">
          <p className="text-xs text-gray-700">
            ⚠️ This is a sample feasibility report for demonstration purposes only. 
            Your actual report will be customized based on your specific property and requirements.
          </p>
        </div>
      </div>
    </div>
  );
}